import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { getCompletedSteps, markStepComplete } from "../../utils";
import { getAmazonConsentUrl } from "../../utils_amazon";
import "./HomeRoadmap.css";

const CALENDLY_URL =
  "https://calendly.com/igprojects/let-s-maximize-your-profits?back=1&month=2026-06";

// Inventory Falcon free-early-access announcement, shown in the banner under
// the header (replaces the weekly live-sessions banner while it runs).
const FALCON_SITE_URL = "https://www.inventoryfalcon.com";

// Categories a new seller is more likely to be ungated in — pre-selected on the
// step-2 "find an ungated product" walkthrough (applied by Products.js on load).
const NEW_SELLER_UNGATED_CATEGORIES = [
  "Arts, Crafts & Sewing",
  "Pet Supplies",
  "Industrial & Scientific",
  "Tools & Home Improvement",
  "Home & Kitchen",
  "Office Products",
];

// Roadmap steps. `target` is a plain string for now (existing pages). When the
// dedicated guides are built, swap each `target` to its /guide/* route — that's
// the only change needed here. `external` steps open in the same/new tab via
// window.open; the rest navigate in-app.
const STEPS = [
  {
    id: "1",
    title: "Connect your Amazon to Nepeto",
    desc: "Link your Amazon Seller account so Nepeto can check gating and more.",
    getAction: () => ({ type: "amazonModal" }),
  },
  {
    id: "2",
    title: "Find an ungated product",
    desc: "Search Nepeto and discover a profitable product you're ungated in.",
    // Land on Retail(OA) and spotlight the "Gated" filter (see GatedGuideTrigger).
    // Pre-select new-seller-friendly categories on arrival.
    getAction: () => ({
      type: "internal",
      url: "/retail?guide=gated",
      presetCategories: NEW_SELLER_UNGATED_CATEGORIES,
    }),
  },
  {
    id: "3",
    title: "Add a product to favorites",
    desc: "Save a product to your favorites so you can find it again fast.",
    // Walkthrough: heart icon -> Dashboard link -> Favorites "Load" (see FavoritesGuideTrigger).
    getAction: () => ({ type: "internal", url: "/retail?guide=favorites" }),
  },
  {
    id: "4",
    title: "Do a storefront scan",
    desc: "Scan a competitor's storefront to uncover products to sell.",
    // Starter plan only -> straight to the scanner; every other plan + free
    // trial -> a /retail walkthrough (see StorefrontGuideTrigger).
    getAction: () => ({ type: "internal", url: "/retail?guide=storefront" }),
  },
  {
    id: "5",
    title: "Download our extension & mail us for access",
    desc: "Get the Nepeto extension, then email us to unlock your access.",
    getAction: () => ({ type: "internal", url: "/app-ext#landingAppExt" }),
  },
  {
    id: "6",
    title: "Schedule a call with us",
    desc: "Head to the Tutorials tab and book a 1-on-1 call with our team.",
    getAction: () => ({ type: "internal", url: "/tutorials" }),
  },
];

// Curated blog previews. One is picked at random on each load.
const BLOGS = [
  {
    title: "Getting Started with Amazon FBA for Beginners",
    href: "/blog/getting-started-with-amazon-fba-for-beginners",
    snippet:
      "New to Amazon FBA? Walk through the essentials — from account setup to your first profitable product.",
    emoji: "🚀",
  },
  {
    title: "How to Find Amazon Arbitrage Products",
    href: "/blog/how-to-find-amazon-arbitrage-products",
    snippet:
      "Practical tactics for sourcing arbitrage products that actually sell and keep your ROI healthy.",
    emoji: "🔍",
  },
  {
    title: "The Best Products for Amazon Arbitrage",
    href: "/blog/the-best-products-for-amazon-arbitrage",
    snippet:
      "A look at the product categories and characteristics that make for winning arbitrage flips.",
    emoji: "📦",
  },
  {
    title: "What Is Nepeto?",
    href: "/blog/what-is-nepeto",
    snippet:
      "See how Nepeto automates product sourcing so you spend less time searching and more time selling.",
    emoji: "🌿",
  },
  {
    title: "Amazon Product Sourcing",
    href: "/blog/amazon-product-sourcing",
    snippet:
      "Everything you need to know about sourcing products for your Amazon business the smart way.",
    emoji: "🧭",
  },
  {
    title: "How to Skyrocket Your Sales as an Amazon FBA Seller",
    href: "/blog/how-to-skyrocket-your-sales-as-an-amazon-fba-seller",
    snippet:
      "Growth strategies to push your Amazon sales to the next level and maximize your profits.",
    emoji: "📈",
  },
  {
    title: "Success in Q4 with Nepeto",
    href: "/blog/success-in-q4-with-nepeto",
    snippet:
      "Q4 is the biggest opportunity of the year — here's how to make the most of it with Nepeto.",
    emoji: "🎯",
  },
];

function HomeRoadmap({ isMobile, user, openUngatedVideo }) {
  const navigate = useNavigate();
  const [completed, setCompleted] = useState(() => getCompletedSteps());
  const [showAmazonModal, setShowAmazonModal] = useState(false);
  // Once every step is done the roadmap collapses to a single "welcome back" bar
  // so returning users land on the content below instead of a finished checklist.
  // It's a toggle, not a one-way door — `expanded` reopens it and nothing is lost.
  const [expanded, setExpanded] = useState(false);

  // Pick a random blog once per mount.
  const blog = useMemo(() => BLOGS[Math.floor(Math.random() * BLOGS.length)], []);

  const isDone = (id) => completed.includes(String(id));

  const handleStepClick = (step) => {
    // Completion is "clicked the step icon" — persist, then act.
    setCompleted(markStepComplete(step.id));
    const action = step.getAction(user);
    if (action.type === "amazonModal") {
      // Same Amazon-sync popup used in the Online Arbitrage Retailers area.
      setShowAmazonModal(true);
    } else if (action.type === "external") {
      window.open(action.url, "_blank", "noopener,noreferrer");
    } else {
      // Stash any preset filters so the target page can apply them on mount.
      // Timestamped: Products.js ignores a stale flag (e.g. the navigation was
      // interrupted), so it can't silently pre-filter an unrelated later visit.
      if (action.presetCategories) {
        sessionStorage.setItem(
          "nepeto_preset_categories",
          JSON.stringify({ cats: action.presetCategories, ts: Date.now() })
        );
      }
      // Client-side nav — NO full reload, keeps Auth0 in-memory session alive.
      navigate(action.url);
      // react-router doesn't auto-scroll to a #hash; do it once the target mounts.
      const hash = action.url.includes("#") ? action.url.split("#")[1] : "";
      if (hash) {
        setTimeout(() => {
          const el = document.getElementById(hash);
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 300);
      }
    }
  };

  const doneCount = STEPS.filter((s) => isDone(s.id)).length;
  const allDone = doneCount === STEPS.length;
  const collapsed = allDone && !expanded;

  // Winding "road map" dashed path (desktop). Points sit at each circle's
  // center; odd steps drop down (see CSS translateY) so the road zig-zags.
  const VB_W = 1200;
  const roadPoints = STEPS.map((_, i) => ({
    x: ((i + 0.5) / STEPS.length) * VB_W,
    y: i % 2 === 0 ? 32 : 104,
  }));
  const roadPath = roadPoints.reduce((d, p, i) => {
    if (i === 0) return `M ${p.x} ${p.y}`;
    const prev = roadPoints[i - 1];
    const midX = (prev.x + p.x) / 2;
    return `${d} C ${midX} ${prev.y} ${midX} ${p.y} ${p.x} ${p.y}`;
  }, "");

  return (
    <div className="roadmap-page montserrat">
      <Helmet>
        <title>Your Nepeto Roadmap</title>
        <meta name="description" content="Your step-by-step roadmap to get started and profit with Nepeto." />
      </Helmet>

      {collapsed ? (
        /* All steps done -> collapse to a single bar so returning users get the
           content below, not a finished checklist. Reopenable via the toggle. */
        <div className="roadmap-header">
          <h1 className="roadmap-title">
            Welcome back{user && user.name ? `, ${user.name.split(" ")[0]}` : ""}! 👋
          </h1>
          <p className="roadmap-subtitle">
            You've completed all {STEPS.length} roadmap steps. 🎉{" "}
            <button type="button" className="roadmap-toggle" onClick={() => setExpanded(true)}>
              Show the roadmap
            </button>
          </p>
        </div>
      ) : (
        <div className="roadmap-header">
          <h1 className="roadmap-title">
            {allDone ? "Welcome back" : "Welcome"}{user && user.name ? `, ${user.name.split(" ")[0]}` : ""}! 👋
          </h1>
          <p className="roadmap-subtitle">
            Follow these {STEPS.length} steps to get the most out of Nepeto. Click a step to begin.
          </p>
          <div className="roadmap-progress">
            <div className="roadmap-progress-bar">
              <div
                className="roadmap-progress-fill"
                style={{ width: `${(doneCount / STEPS.length) * 100}%` }}
              />
            </div>
            <span className="roadmap-progress-text">{doneCount}/{STEPS.length} completed</span>
          </div>
          {allDone && (
            <p className="roadmap-subtitle">
              🎉 All done!{" "}
              <button type="button" className="roadmap-toggle" onClick={() => setExpanded(false)}>
                Hide the roadmap
              </button>
            </p>
          )}
        </div>
      )}

      {/* Inventory Falcon early-access announcement — remove this block (and
          the falcon-* styles) when the promo ends. */}
      <div className="roadmap-falcon">
        <div className="roadmap-falcon-header">
          <span className="roadmap-falcon-dot" aria-hidden="true" />
          <span className="roadmap-falcon-eyebrow">Announcement</span>
        </div>
        <div className="roadmap-falcon-card">
          <span className="roadmap-falcon-badge">🦅 Free Early Access</span>
          <span className="roadmap-falcon-when">New from the Nepeto team</span>
          <h3 className="roadmap-falcon-title">Inventory Falcon — Free Early Access</h3>
          <p className="roadmap-falcon-desc">
            Inventory Falcon is our inventory management software for Walmart
            sellers: bulk-upload products, track profit per SKU, and create WFS
            shipments from one dashboard. Early access to our new version is completely free —
            subscribe to get in first.
          </p>
          <div className="roadmap-falcon-actions">
            <a
              href={FALCON_SITE_URL}
              target="_blank"
              rel="noreferrer"
              className="roadmap-btn roadmap-btn--light roadmap-falcon-btn"
            >
              Get Free Early Access
            </a>
          </div>
        </div>
      </div>

      {!collapsed && (
      <div className={`roadmap-track ${isMobile ? "is-vertical" : "is-winding"}`}>
        {!isMobile && (
          <svg
            className="roadmap-road"
            viewBox={`0 0 ${VB_W} 136`}
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d={roadPath}
              fill="none"
              stroke="#8c3e7c"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="9 9"
            />
          </svg>
        )}
        {STEPS.map((step, idx) => (
          <div className="roadmap-step" key={step.id}>
            <button
              type="button"
              className={`roadmap-circle ${isDone(step.id) ? "done" : ""} ${
                completed.length === 0 && idx === 0 ? "attention" : ""
              }`}
              onClick={() => handleStepClick(step)}
              aria-label={step.title}
            >
              {isDone(step.id) ? <i className="fa fa-check" aria-hidden="true" /> : step.id}
            </button>
            <div className="roadmap-step-body">
              <h3
                className="roadmap-step-title"
                onClick={() => handleStepClick(step)}
              >
                {step.title}
              </h3>
              <p className="roadmap-step-desc">{step.desc}</p>
            </div>
          </div>
        ))}
      </div>
      )}

      {/* "Secret" completion reward — only appears once every step is done. */}
      {allDone && (
        <div className="roadmap-reward">
          <span className="roadmap-reward-emoji" aria-hidden="true">🔓</span>
          <div className="roadmap-reward-body">
            <h2 className="roadmap-reward-title">You unlocked a bonus!</h2>
            <p className="roadmap-reward-text">
              Not sure how to find ungated products using Nepeto?
            </p>
          </div>
          <button
            type="button"
            className="roadmap-btn roadmap-reward-btn"
            onClick={() => openUngatedVideo?.()}
          >
            Click here
          </button>
        </div>
      )}

      {/* --- Below the roadmap --- */}
      <div className="roadmap-below">
        {/* Tutorials */}
        <div className="roadmap-card">
          <h2 className="roadmap-card-title">📚 Tutorials</h2>
          <p className="roadmap-card-text">
            Watch hands-on tutorials and webinars to learn how to source, scan, and scale with Nepeto.
          </p>
          <a href="/tutorials" className="roadmap-btn">Open Tutorials</a>
        </div>

        {/* Schedule a call */}
        <div className="roadmap-card roadmap-card--accent">
          <h2 className="roadmap-card-title" style={{ color: "white" }}>📅 Schedule a Call With Us</h2>
          <p className="roadmap-card-text" style={{ color: "white" }}>
            Book a personal 1-on-1 session to talk about maximizing your profits and getting the most out of Nepeto.
          </p>
          <button
            type="button"
            className="roadmap-btn roadmap-btn--light"
            onClick={() => window.open(CALENDLY_URL, "_blank", "noopener,noreferrer")}
          >
            Schedule My Call
          </button>
        </div>

        {/* Random blog preview */}
        <a className="roadmap-card roadmap-blog" href={blog.href}>
          <div className="roadmap-blog-thumb">{blog.emoji}</div>
          <div className="roadmap-blog-body">
            <span className="roadmap-blog-eyebrow">From our blog</span>
            <h2 className="roadmap-card-title">{blog.title}</h2>
            <p className="roadmap-card-text">{blog.snippet}</p>
            <span className="roadmap-btn roadmap-btn--ghost">Read article →</span>
          </div>
        </a>
      </div>

      {/* Amazon-sync popup — same one shown in the Online Arbitrage Retailers area. */}
      {showAmazonModal && (
        <div id="modal-js-example" className="modal is-active">
          <div className="modal-background" onClick={() => setShowAmazonModal(false)}></div>
          <div className="modal-content" style={{ width: isMobile ? "90vw" : "38vw" }}>
            <div className="box" style={{ textAlign: "center", fontWeight: "600" }}>
              <p style={{ fontSize: isMobile ? "2.5vh" : "1.1vw" }}>
                Log in to your Amazon account to automate the eligibility check!
              </p>
              <br />
              <i
                className="fa fa-unlock graphicon tooltip"
                style={{ width: "2.25vw", height: "2.25vw", borderRadius: "20px", border: "2px solid gray", padding: "0.3vw 0.2vw", fontSize: "1.6vw", color: "white", backgroundColor: "green" }}
              />&emsp;GREEN - You CAN sell this item!
              <br /><br />
              <i
                className="fa fa-lock graphicon tooltip"
                style={{ width: "2.25vw", height: "2.25vw", borderRadius: "20px", border: "2px solid gray", padding: "0.3vw 0.2vw", fontSize: "1.6vw", color: "white", backgroundColor: "red" }}
              />&emsp;RED - You CANNOT sell this item!
              <br /><br />
              <i
                className="fa fa-unlock-alt graphicon tooltip"
                style={{ width: "2.25vw", height: "2.25vw", borderRadius: "20px", border: "2px solid gray", padding: "0.3vw 0.2vw", fontSize: "1.6vw", color: "white", backgroundColor: "orange" }}
              />&emsp;ORANGE - You need to APPLY to sell this item!
              <br /><br /><br />
              <i className="fa fa-arrow-right" aria-hidden="true" style={{ fontSize: "25px", marginRight: "10px" }}></i>
              {user && user.email && (
                <img
                  alt="Login with Amazon"
                  src="https://images-na.ssl-images-amazon.com/images/G/01/lwa/btnLWA_gold_156x32.png"
                  onClick={() => window.open(getAmazonConsentUrl(user.email))}
                  style={{ resizeMode: "center", cursor: "pointer", boxShadow: "0px 0px 5px 0px gray", borderRadius: "5px", marginBottom: "-8px" }}
                />
              )}
              <i className="fa fa-arrow-left" aria-hidden="true" style={{ fontSize: "25px", marginLeft: "10px" }}></i>
              <br /><br /><br />
              <div className="columns is-mobile is-centered" style={{ marginBottom: "1%", marginTop: "1%" }}>
                <button
                  className="button is-rounded is-danger"
                  style={{ fontSize: isMobile ? "1.9vh" : ".95vw" }}
                  onClick={() => setShowAmazonModal(false)}
                >
                  Close for Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HomeRoadmap;
